/**
 * Global App Alert - Ultra-Modern Glassmorphic Modal & Notification System
 * Replaces native browser window.alert() with premium Day/Night responsive dialogs.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';

let _listener = null;
const _pendingQueue = [];

// Best-effort type detection from the message
function _detectType(text) {
  const t = (text || '').toLowerCase();
  if (/(success|successfully|completed|saved|updated|confirmed|sent|released|authorized|✓|✅)/.test(t)) return 'success';
  if (/(fail|failed|error|invalid|unable|could not|couldn't|not before|too early|denied|unauthor)/.test(t)) return 'error';
  if (/(required|please|select|enter|upload|missing|must|verify|warning|are you sure)/.test(t)) return 'warning';
  return 'info';
}

function _coerce(message) {
  if (typeof message === 'string') return message;
  if (!message) return 'Something went wrong.';
  return (
    message?.response?.data?.error ||
    message?.response?.data?.message ||
    message?.message ||
    String(message)
  );
}

/** Show the modal. Returns nothing. Safe to call anywhere. */
export function showAlert(message, opts = {}) {
  const text = _coerce(message);
  const payload = {
    id: Date.now() + Math.random(),
    message: text,
    title: opts.title || '',
    type: opts.type || _detectType(text),
    okLabel: opts.okLabel || 'Got It',
    onOk: typeof opts.onOk === 'function' ? opts.onOk : null,
  };
  if (_listener) {
    _listener(payload);
  } else {
    _pendingQueue.push(payload);
  }
}

// Global monkey-patch: replace native window.alert so browser popups never show
if (typeof window !== 'undefined' && !window._musbAlertPatched) {
  window._musbAlertPatched = true;
  window._origAlert = window.alert;
  window.alert = function(msg) {
    showAlert(msg);
  };
}

const TONES = {
  success: {
    color: '#10b981',
    bg: 'rgba(16, 185, 129, 0.14)',
    border: 'rgba(16, 185, 129, 0.28)',
    btnBg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    fallbackTitle: 'Action Completed',
    Icon: CheckCircle2
  },
  error: {
    color: '#f43f5e',
    bg: 'rgba(244, 63, 94, 0.14)',
    border: 'rgba(244, 63, 94, 0.28)',
    btnBg: 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)',
    fallbackTitle: 'Attention Required',
    Icon: AlertCircle
  },
  warning: {
    color: '#f59e0b',
    bg: 'rgba(245, 158, 11, 0.14)',
    border: 'rgba(245, 158, 11, 0.28)',
    btnBg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    fallbackTitle: 'Please Review',
    Icon: AlertTriangle
  },
  info: {
    color: '#6366f1',
    bg: 'rgba(99, 102, 241, 0.14)',
    border: 'rgba(99, 102, 241, 0.28)',
    btnBg: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
    fallbackTitle: 'Diagnostic Notice',
    Icon: Info
  },
};

const CSS = `
@keyframes appAlertFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes appAlertPop { from { opacity: 0; transform: translateY(14px) scale(.95); } to { opacity: 1; transform: none; } }

.app-alert-backdrop {
  position: fixed;
  inset: 0;
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(4, 9, 20, 0.78);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  animation: appAlertFade .18s cubic-bezier(0.16, 1, 0.3, 1);
}

.app-alert-card {
  width: 100%;
  max-width: 440px;
  background: #0c1322 !important;
  color: #ffffff !important;
  border-radius: 24px;
  padding: 2.2rem 2rem 1.8rem;
  box-shadow: 0 30px 70px -15px rgba(0, 0, 0, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  text-align: center;
  animation: appAlertPop .24s cubic-bezier(.16, 1, .3, 1);
  position: relative;
  box-sizing: border-box;
}

.app-alert-close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 6px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all .15s ease;
}
.app-alert-close-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
}

.app-alert-badge {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.25rem;
  box-shadow: 0 8px 24px -4px rgba(0, 0, 0, 0.3);
}

.app-alert-title {
  font-size: 1.28rem !important;
  font-weight: 800 !important;
  margin: 0 0 .65rem !important;
  letter-spacing: -0.02em;
  color: #ffffff !important;
  line-height: 1.3;
}

.app-alert-msg {
  font-size: .94rem !important;
  line-height: 1.6;
  color: #cbd5e1 !important;
  margin: 0 0 1.6rem !important;
  white-space: pre-line;
  word-break: break-word;
}

.app-alert-btn {
  width: 100%;
  border: none;
  border-radius: 14px;
  padding: .92rem 1.2rem;
  font-size: .94rem !important;
  font-weight: 800 !important;
  color: #ffffff !important;
  cursor: pointer;
  transition: all .18s ease;
  letter-spacing: 0.3px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
}
.app-alert-btn:hover {
  filter: brightness(1.1);
  transform: translateY(-1px);
}
.app-alert-btn:active {
  transform: translateY(1px);
}

/* Day / Light Mode Styling */
.app-alert-card.light-theme,
body.light-mode .app-alert-card,
.theme-light .app-alert-card,
.light .app-alert-card {
  background: #ffffff !important;
  color: #0f172a !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 25px 60px -15px rgba(15, 23, 42, 0.22) !important;
}

.app-alert-card.light-theme .app-alert-title,
body.light-mode .app-alert-title,
.theme-light .app-alert-title,
.light .app-alert-title {
  color: #0f172a !important;
  text-shadow: none !important;
}

.app-alert-card.light-theme .app-alert-msg,
body.light-mode .app-alert-msg,
.theme-light .app-alert-msg,
.light .app-alert-msg {
  color: #475569 !important;
}

.app-alert-card.light-theme .app-alert-close-btn,
body.light-mode .app-alert-close-btn,
.theme-light .app-alert-close-btn,
.light .app-alert-close-btn {
  color: #64748b !important;
}
.app-alert-card.light-theme .app-alert-close-btn:hover,
body.light-mode .app-alert-close-btn:hover,
.theme-light .app-alert-close-btn:hover,
.light .app-alert-close-btn:hover {
  background: #f1f5f9 !important;
  color: #0f172a !important;
}
`;

export function AppAlertHost() {
  const [queue, setQueue] = useState([]);
  const [isLightMode, setIsLightMode] = useState(false);
  const current = queue[0] || null;

  // Sync theme
  useEffect(() => {
    const checkTheme = () => {
      const saved = localStorage.getItem('musb_theme_mode');
      if (saved) {
        setIsLightMode(saved === 'light');
        return;
      }
      const hasLightClass = (
        document.body.classList.contains('light-mode') ||
        document.querySelector('.theme-light') !== null ||
        document.querySelector('.light') !== null
      );
      setIsLightMode(hasLightClass);
    };

    checkTheme();
    window.addEventListener('storage', checkTheme);
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => {
      window.removeEventListener('storage', checkTheme);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    _listener = (payload) => setQueue(q => [...q, payload]);
    if (_pendingQueue.length > 0) {
      setQueue(q => [...q, ..._pendingQueue]);
      _pendingQueue.length = 0;
    }
    return () => { _listener = null; };
  }, []);

  const close = useCallback(() => {
    setQueue(q => {
      const done = q[0];
      if (done && done.onOk) { try { done.onOk(); } catch (e) { /* noop */ } }
      return q.slice(1);
    });
  }, []);

  useEffect(() => {
    if (!current) return undefined;
    const onKey = (e) => {
      if (e.key === 'Enter' || e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current, close]);

  if (!current) return null;

  const tone = TONES[current.type] || TONES.info;
  const title = current.title || tone.fallbackTitle;
  const BadgeIcon = tone.Icon;

  return createPortal(
    <>
      <style>{CSS}</style>
      <div className="app-alert-backdrop" onClick={close} role="alertdialog" aria-modal="true">
        <div className={`app-alert-card ${isLightMode ? 'light-theme' : 'dark-theme'}`} onClick={e => e.stopPropagation()}>
          <button type="button" className="app-alert-close-btn" onClick={close} title="Close">
            <X size={18} />
          </button>
          <div className="app-alert-badge" style={{ background: tone.bg, border: `1px solid ${tone.border}`, color: tone.color }}>
            <BadgeIcon size={32} />
          </div>
          <h3 className="app-alert-title">{title}</h3>
          <p className="app-alert-msg">{current.message}</p>
          <button
            type="button"
            className="app-alert-btn"
            style={{ background: tone.btnBg }}
            onClick={close}
            autoFocus
          >
            {current.okLabel}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}

export default showAlert;
